import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader } from '../ui/card';

const TechStack = ({ data = {} }) => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });

  const categories = [
    { 
      title: 'Frontend Development', 
      icon: '⚡', 
      techs: data.frontend || [], 
      color: 'bg-blue-900/30 border-blue-700/50',
      hoverColor: 'hover:border-blue-400/70',
      glowColor: 'from-blue-500/20 to-blue-600/20'
    },
    { 
      title: 'Backend & APIs', 
      icon: '🗄️', 
      techs: data.backend || [], 
      color: 'bg-purple-900/30 border-purple-700/50',
      hoverColor: 'hover:border-purple-400/70',
      glowColor: 'from-purple-500/20 to-purple-600/20'
    },
    { 
      title: 'Cloud & DevOps', 
      icon: '☁️', 
      techs: data.cloud || [], 
      color: 'bg-emerald-900/30 border-emerald-700/50',
      hoverColor: 'hover:border-emerald-400/70',
      glowColor: 'from-emerald-500/20 to-emerald-600/20'
    },
    { 
      title: 'Testing & Development', 
      icon: '🧪', 
      techs: data.testing || [], 
      color: 'bg-orange-900/30 border-orange-700/50',
      hoverColor: 'hover:border-orange-400/70',
      glowColor: 'from-orange-500/20 to-orange-600/20'
    },
    { 
      title: 'AI & LLM Tools', 
      icon: '🤖', 
      techs: data.ai || [], 
      color: 'bg-pink-900/30 border-pink-700/50',
      hoverColor: 'hover:border-pink-400/70',
      glowColor: 'from-pink-500/20 to-pink-600/20'
    },
    { 
      title: 'Design & Tools', 
      icon: '🛠️', 
      techs: data.tools || [], 
      color: 'bg-gray-900/30 border-gray-700/50',
      hoverColor: 'hover:border-gray-400/70',
      glowColor: 'from-gray-500/20 to-gray-600/20'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.3
      }
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0, scale: 0.9 },
    visible: {
      y: 0,
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
        type: "spring",
        stiffness: 100
      }
    }
  };

  return (
    <section id="tech-stack" className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-900/30" ref={ref}>
      <motion.div 
        className="max-w-6xl mx-auto"
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <div className="text-center mb-20">
          <h2 className="text-5xl sm:text-6xl font-bold text-white mb-4 tracking-tight">
            Tech Stack
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-400 mx-auto mb-6" />
          <p className="text-lg text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Technologies and methodologies I use to build scalable, performant applications
          </p>
        </div>

        <motion.div 
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={{
            hidden: {},
            visible: {
              transition: {
                staggerChildren: 0.1
              }
            }
          }}
        >
          {categories.map((category, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              whileHover={{ 
                y: -10,
                scale: 1.05,
                rotateY: 5,
                rotateX: 5
              }}
              transition={{ 
                duration: 0.3,
                type: "spring",
                stiffness: 300,
                damping: 30
              }}
              className="group cursor-pointer"
              style={{ transformStyle: "preserve-3d" }}
            >
              <Card className="bg-gray-900/40 border border-gray-800/50 hover:border-gray-700/80 transition-all duration-300 relative overflow-hidden h-full hover:shadow-lg hover:shadow-emerald-500/5">
                <CardHeader className="pb-3 relative z-10">
                  <h3 className="text-lg font-semibold text-white flex items-center tracking-tight">
                    <span className="mr-3 text-xl">
                      {category.icon}
                    </span>
                    {category.title}
                  </h3>
                </CardHeader>
                
                <CardContent className="relative z-10">
                  <div className="flex flex-wrap gap-2">
                    {category.techs.map((tech, techIndex) => (
                      <Badge 
                        key={techIndex}
                        className="bg-gray-800/60 text-gray-300 hover:bg-gray-700/60 transition-colors text-xs border border-gray-700/50 px-2.5 py-1"
                      >
                        {tech}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default TechStack;